/* eslint-disable react/jsx-props-no-spreading */
import * as z from 'zod';

import {
  Button,
  Flex,
  FormControl,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';

import { PartnerLoginConfig } from '@/client/types/preauth/Preauth';
import { Link as RouterLink } from 'react-router-dom';
import { useLogin } from '@/client/services/hooks/preauth/useLogin';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

interface LoginFormProps {
  userIdPattern: string;
  userIdLimit: number;
  partnerLoginConfig?: PartnerLoginConfig;
}

export default function LoginForm({
  userIdPattern,
  userIdLimit,
  partnerLoginConfig,
}: LoginFormProps) {
  const { t } = useTranslation();
  const { login, loginLoading } = useLogin();
  const [showPassword, setShowPassword] = useState(false);

  const formSchema = z.object({
    userIdentifier: z
      .string()
      .email()
      .or(z.string().regex(new RegExp(userIdPattern)).max(userIdLimit)),
    password: z.string().min(1),
  });

  const {
    watch,
    handleSubmit,
    register,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userIdentifier: '',
      password: '',
    },
    mode: 'all',
  });

  const { userIdentifier, password } = watch();

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) =>
    login({
      userid: data.userIdentifier,
      password: data.password,
      loginType: 'email',
      partnerLoginConfig,
      handleError: () => {
        setError('userIdentifier', {});
        setError('password', {});
        setError('root.invalidCredentials', {});
      },
    });

  useEffect(() => {
    if (errors?.root?.invalidCredentials) clearErrors();
  }, [userIdentifier, password]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <Stack>
          <FormControl isInvalid={!!errors.userIdentifier}>
            <Input
              {...register('userIdentifier')}
              variant="neutral"
              placeholder={`${t('login.identifier.placeholder')}`}
            />

            {errors?.userIdentifier && !errors?.root?.invalidCredentials && (
              <Text fontSize="14px" fontWeight={500} color="error">
                {t('login.invalidEmailOrUsername')}
              </Text>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.password}>
            <InputGroup>
              <Input
                {...register('password')}
                variant="neutral"
                type={showPassword ? 'text' : 'password'}
                placeholder={`${t('global.forms.labels_password')}`}
              />

              <InputRightElement>
                <IconButton
                  variant="ghost"
                  aria-label="Toggle password visibility"
                  icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                  color="neutral.700"
                  onClick={() => setShowPassword(!showPassword)}
                  _active={{ backgroundColor: 'transparent' }}
                  _focus={{ backgroundColor: 'transparent' }}
                  _hover={{ backgroundColor: 'transparent' }}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <Flex justifyContent="flex-end">
            <Link as={RouterLink} to="/forgot" variant="neutral" fontSize="14px">
              {t('login.forgotPassword')}
            </Link>
          </Flex>

          {errors?.root?.invalidCredentials && (
            <Text fontSize="14px" fontWeight={500} color="error">
              {t('login.invalidCredentials')}
            </Text>
          )}
        </Stack>

        <Button type="submit" isDisabled={!isValid} isLoading={loginLoading}>
          {t('preauth.signin.button')}
        </Button>
      </Stack>
    </form>
  );
}
